import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Clock, Users, Monitor, Check, ArrowUpRight, Building2 } from "lucide-react";
import UpcomingDates from "@/components/UpcomingDates";
import Venues from "@/components/Venues";
import AdditionalWorkshops from "@/components/AdditionalWorkshops";
import CtaBanner from "@/components/CtaBanner";

const courses = [
  {
    id: "adult-two-day",
    name: "Adult MHFA — Two Day",
    icon: Users,
    tag: "Flagship Certification",
    ctaType: "dual",
    inhouseKey: "Adult MHFA — Two Day (in-house / private group)",
    intro:
      "The full Mental Health First Aider certification, based on MHFA England's Adult Two Day content. Delivered online as a blended course — individual learning combined with four live instructor-led sessions — or in-person at your workplace.",
    outcomes: [
      "In-depth understanding of mental health and the factors that affect wellbeing",
      "Practical skills to spot the triggers and signs of mental health issues",
      "Confidence to step in, reassure and support a person in distress",
      "Enhanced interpersonal skills including non-judgemental listening",
      "Knowledge to help someone recover their health by guiding them to further support",
    ],
    includes: [
      "Certificate of attendance as a Mental Health First Aider",
      "Comprehensive course manual",
      "Quick reference card",
      "Workbook & post-course access to MHFA England Online Hub",
    ],
    duration: "2 days · 4 live sessions",
    format: "Online or in-person",
    price: "£325 pp",
    priceLabel: "Standard course fee",
    priceNote: "Discounts available for group open course bookings and in-house training.",
  },
  {
    id: "refresher",
    name: "MHFA Refresher",
    icon: Clock,
    tag: "For existing MHFAiders",
    ctaType: "dual",
    inhouseKey: "MHFA Refresher (in-house / private group)",
    intro:
      "A half-day course for anyone who has previously completed a 1-day or 2-day MHFA course. Update your knowledge, refresh your skills, and share experiences with other Mental Health First Aiders in a supportive setting.",
    outcomes: [
      "Refresh how to spot signs of poor mental health",
      "Practice active listening and conversation skills",
      "Update knowledge with the latest MHFA guidance",
      "Learn from other MHFAiders' real-world experiences",
    ],
    includes: [
      "Interactive online session with film clips and case studies",
      "Skills practice exercises",
      "Updated resources & signposting",
    ],
    duration: "Half day",
    format: "Online or in-person (in-house)",
    price: "£150 pp",
    priceLabel: "Standard course fee",
    priceNote: "Discounts available for group open course bookings and in-house training.",
  },
  {
    id: "aware",
    name: "MHFA Aware",
    icon: Monitor,
    tag: "Awareness for all",
    ctaType: "enquire",
    inhouseKey: "MHFA Aware (in-house)",
    intro:
      "An introductory half-day course delivered as private in-house training for your organisation. Perfect for teams wanting every employee to have foundational mental health awareness — not just their dedicated first aiders.",
    outcomes: [
      "Understanding of common mental health issues",
      "Confidence to have supportive conversations with colleagues",
      "Awareness of your own mental wellbeing",
      "Where to signpost people for further help",
    ],
    includes: [
      "Interactive online or in-person session",
      "Awareness certificate",
      "Take-away resources",
    ],
    duration: "Half day",
    format: "Online or in-person · By enquiry",
    price: "By enquiry",
    priceLabel: "Group pricing",
    priceNote: "",
  },
];

export default function Courses() {
  return (
    <div data-testid="courses-page" className="pt-36">
      <div className="container-x">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <p className="eyebrow mb-4">Our Courses</p>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.02] tracking-tight text-[#1E2A25] max-w-4xl">
            Accredited MHFA training, <span className="italic text-[#4A5D52]">delivered with care.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-[#4A5D52] leading-relaxed">
            Every course is accredited by MHFA England and personally delivered by Liz Milligan.
            Book a spot on an upcoming open course, or arrange <strong className="text-[#1E2A25] font-medium">private in-house training</strong> —
            online or in person — for your team.
          </p>
        </motion.div>
      </div>

      <div className="container-x mt-24 space-y-32">
        {courses.map((c, i) => {
          const Icon = c.icon;
          return (
            <motion.article
              key={c.id}
              data-testid={`course-detail-${c.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7 }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-12"
            >
              <div className="lg:col-span-4">
                <div className="sticky top-32">
                  <span className="inline-flex w-14 h-14 rounded-full bg-[#4A5D52]/10 text-[#4A5D52] items-center justify-center mb-6">
                    <Icon className="w-6 h-6" strokeWidth={1.5} />
                  </span>
                  <span className="eyebrow text-[#D88C76]">{c.tag}</span>
                  <h2 className="font-serif-display text-4xl md:text-5xl mt-4 text-[#1E2A25] leading-tight">{c.name}</h2>

                  <dl className="mt-8 space-y-3 text-[#4A5D52]">
                    <div className="flex gap-4"><dt className="w-24 text-sm eyebrow">Length</dt><dd>{c.duration}</dd></div>
                    <div className="flex gap-4"><dt className="w-24 text-sm eyebrow">Format</dt><dd>{c.format}</dd></div>
                    <div className="flex gap-4"><dt className="w-24 text-sm eyebrow">{c.priceLabel}</dt><dd className="text-[#1E2A25] font-medium">{c.price}</dd></div>
                  </dl>
                  {c.priceNote && (
                    <p className="mt-3 text-xs text-[#4A5D52]/85 italic leading-relaxed max-w-xs">
                      {c.priceNote}
                    </p>
                  )}

                  <div className="mt-8 flex flex-col gap-3">
                    {c.ctaType === "dual" ? (
                      <>
                        <Link
                          to={`/contact?course=${encodeURIComponent(c.inhouseKey)}`}
                          data-testid={`course-inhouse-${c.id}`}
                          className="btn-primary"
                        >
                          <Building2 className="w-4 h-4" />
                          In-house training
                        </Link>
                        <a
                          href="#upcoming-courses"
                          data-testid={`course-cta-${c.id}`}
                          className="btn-secondary"
                        >
                          Upcoming open courses <ArrowUpRight className="w-4 h-4" />
                        </a>
                      </>
                    ) : (
                      <Link
                        to={`/contact?course=${encodeURIComponent(c.inhouseKey)}`}
                        data-testid={`course-cta-${c.id}`}
                        className="btn-primary"
                      >
                        Enquire with us <ArrowUpRight className="w-4 h-4" />
                      </Link>
                    )}
                    {c.id === "adult-two-day" && null}
                  </div>
                </div>
              </div>

              <div className="lg:col-span-8 space-y-10">
                <p className="text-lg text-[#1E2A25]/85 leading-relaxed">{c.intro}</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="bg-[#F3EFEA] rounded-3xl p-8">
                    <h4 className="eyebrow text-[#4A5D52] mb-6">What you&apos;ll learn</h4>
                    <ul className="space-y-3">
                      {c.outcomes.map((o, idx) => (
                        <li key={idx} className="flex gap-3 text-[#1E2A25]/85">
                          <Check className="w-4 h-4 mt-1 text-[#D88C76] flex-shrink-0" />
                          <span>{o}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-[#FDFBF7] rounded-3xl p-8 border border-[#8DA399]/25">
                    <h4 className="eyebrow text-[#4A5D52] mb-6">What&apos;s included</h4>
                    <ul className="space-y-3">
                      {c.includes.map((o, idx) => (
                        <li key={idx} className="flex gap-3 text-[#1E2A25]/85">
                          <Check className="w-4 h-4 mt-1 text-[#4A5D52] flex-shrink-0" />
                          <span>{o}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.article>
          );
        })}
      </div>

      <div className="mt-24">
        <UpcomingDates />
        <Venues />
        <AdditionalWorkshops />
        <CtaBanner />
      </div>
    </div>
  );
}
